import { RawToolItem, ToolItem } from '../types';
import { getToolSeoConfig } from './toolsSeoData';

export const PHASE_NAMES: Record<number, string> = {
  1: 'PHASE 1 — LOVE CALCULATORS',
  2: 'PHASE 2 — COMPATIBILITY TESTS',
  3: 'PHASE 3 — COUPLE QUIZZES',
  4: 'PHASE 4 — LOVE LANGUAGES & PERSONALITY',
  5: 'PHASE 5 — COUPLE GAMES & QUESTIONS',
  6: 'PHASE 6 — RELATIONSHIP CALCULATORS',
};

export const CATEGORIES = [
  'Love Tests',
  'Compatibility',
  'Couple Quizzes',
  'Love Languages',
  'Personality',
  'Couple Games',
  'Calculators',
] as const;

export const TOOLS_DATA: RawToolItem[] = [
  // ==========================================
  // PHASE 1: LOVE CALCULATORS (1 - 16)
  // ==========================================
  {
    id: 'love-calculator',
    name: 'Love Calculator',
    number: 1,
    phase: 1,
    phaseName: PHASE_NAMES[1],
    category: 'Love Tests',
    tagline: 'Calculate your love percentage and relationship chemistry in seconds',
    description: 'Enter two names to calculate their romantic synergy, harmonic chemistry, and long-term potential score.',
    detailedOverview: 'Our Love Calculator uses an algorithmic name resonance formula combined with phonetic synergy metrics to generate a fun, delightful compatibility reading. It provides a multi-factor breakdown across romance, communication, and longevity.',
    howItWorks: [
      'Enter your name and your partner’s or crush’s name',
      'The engine computes algorithmic character harmony and vibrational compatibility',
      'Receive your Love Score %, romantic chemistry meter, and fun custom relationship insights'
    ],
    whatResultMeans: 'High percentages indicate playful romantic resonance and harmonious vibrations. Remember, this test is created for entertainment, celebration, and sparking joyful conversations!',
    icon: 'Heart',
    isFunctional: true,
    badge: 'Popular',
    tags: ['love test', 'name calculator', 'crush match', 'romance score', 'flames'],
    entertainmentDisclaimer: true,
    faqs: [
      {
        question: 'Is the Love Calculator accurate for real relationships?',
        answer: 'The Love Calculator uses character resonance and name vibration algorithms designed strictly for entertainment and fun. True relationships thrive on communication, mutual respect, and trust!'
      },
      {
        question: 'Can I use nicknames or middle names?',
        answer: 'Yes! You can test nicknames, full names, or affectionate pet names to see how the algorithmic score varies.'
      },
      {
        question: 'Does LoveScoreTest store my names or data?',
        answer: 'No. All calculations are executed directly in your browser on the client side. Your names are never saved, transmitted, or logged.'
      }
    ]
  },
  {
    id: 'love-compatibility-test',
    name: 'Love Compatibility Test',
    number: 2,
    phase: 2,
    phaseName: PHASE_NAMES[2],
    category: 'Compatibility',
    tagline: 'Deep multi-dimensional compatibility assessment for couples',
    description: 'Evaluate core values, communication styles, emotional connection, and future goals across 8 essential dimensions.',
    detailedOverview: 'Unlike superficial quizzes, this test analyzes eight core pillars of long-term relationship happiness: emotional vulnerability, conflict resolution, lifestyle habits, humor, financial harmony, and shared vision.',
    howItWorks: [
      'Answer 8 thoughtful questions regarding relationship scenarios and expectations',
      'The system evaluates dimensional alignment across emotional, practical, and romantic vectors',
      'Review a detailed compatibility breakdown with tailored connection advice'
    ],
    whatResultMeans: 'Scores above 80% indicate exceptional baseline alignment. Lower scores highlight opportunities for growth, dialogue, and deeper understanding.',
    icon: 'Sparkles',
    isFunctional: true,
    badge: 'Trending',
    tags: ['compatibility test', 'deep quiz', 'values match', 'long term love'],
    faqs: [
      {
        question: 'Can we take this test together?',
        answer: 'Yes! It is best taken together or independently followed by comparing your answers to spark meaningful conversations.'
      },
      {
        question: 'What if our compatibility score is lower than expected?',
        answer: 'Differences make relationships rich and resilient. Lower scores pinpoint areas where intentional communication can create breakthroughs.'
      }
    ]
  },
  {
    id: 'friendship-compatibility-test',
    name: 'Friendship Compatibility Test',
    number: 3,
    phase: 2,
    phaseName: PHASE_NAMES[2],
    category: 'Compatibility',
    tagline: 'Discover your platonic bond strength and friendship archetype',
    description: 'Measure loyalty, humor, emotional support, and shared values to see how strong your friendship bond is.',
    detailedOverview: 'Friendships are vital to our emotional well-being. This tool analyzes the mutual trust, listening habits, banter, and crisis reliability that make great friendships last a lifetime.',
    howItWorks: [
      'Respond to 6 scenarios covering support in tough moments, humor, boundaries, and shared memories',
      'The engine computes your Platonic Synergy Index and identifies your Friendship Archetype',
      'Get suggestions on how to celebrate and strengthen your connection'
    ],
    whatResultMeans: 'Your friendship archetype (e.g. Soul Siblings, The Adventure Duo, Anchor Friends) reflects how you best support and inspire one another.',
    icon: 'Users',
    isFunctional: true,
    badge: 'Popular',
    tags: ['best friends', 'bff quiz', 'friendship score', 'platonic match'],
    faqs: [
      {
        question: 'Can this test be used for new friends or old friends?',
        answer: 'Both! It is equally revealing for lifelong best friends or growing friendships.'
      }
    ]
  },
  {
    id: 'couple-compatibility-quiz',
    name: 'Couple Compatibility Quiz',
    number: 4,
    phase: 3,
    phaseName: PHASE_NAMES[3],
    category: 'Couple Quizzes',
    tagline: 'Test your romantic bond, conflict style, and intimate connection',
    description: 'A comprehensive quiz exploring romance, trust, stress handling, and shared vision for dating or married couples.',
    detailedOverview: 'Created for couples who want to explore the depth of their relationship. It balances fun questions with reflective inquiries into how you handle disagreements and celebrate victories.',
    howItWorks: [
      'Complete 7 insightful questions about your dynamic as a couple',
      'The algorithm measures emotional safety, romance maintenance, and partnership equality',
      'Receive a Bond Strength Grade, relationship superpower, and actionable date ideas'
    ],
    whatResultMeans: 'Your results illuminate your couple superpower (e.g. Unshakable Team, Romantic Dreamers) and suggest areas to nurture further.',
    icon: 'HeartHandshake',
    isFunctional: true,
    badge: 'Top Pick',
    tags: ['couple test', 'relationship strength', 'dating quiz', 'marriage test'],
    faqs: [
      {
        question: 'Is this suitable for newly dating couples?',
        answer: 'Yes! It helps new couples discover their dynamic early on and provides great date night conversation topics.'
      }
    ]
  },
  {
    id: 'love-language-test',
    name: 'Love Language Test',
    number: 5,
    phase: 4,
    phaseName: PHASE_NAMES[4],
    category: 'Love Languages',
    tagline: 'Discover your primary and secondary love languages',
    description: 'Find out how you most naturally give and receive affection across the 5 recognized love languages.',
    detailedOverview: 'Based on the five classic love languages (Words of Affirmation, Acts of Service, Receiving Gifts, Quality Time, and Physical Touch), this test pinpoints your unique affection profile with percentage scores.',
    howItWorks: [
      'Select your heartfelt preferences across 10 distinct relationship situations',
      'The test tallies weighted scores across all 5 affection channels',
      'Get your Primary & Secondary Love Language breakdown with practical partner tips'
    ],
    whatResultMeans: 'Your primary language is your heart’s main emotional dialect. Understanding it helps you express your needs and appreciate your partner’s gestures.',
    icon: 'MessageCircleHeart',
    isFunctional: true,
    badge: 'Popular',
    tags: ['5 love languages', 'words of affirmation', 'quality time', 'physical touch', 'acts of service'],
    faqs: [
      {
        question: 'Can a person have more than one primary love language?',
        answer: 'Yes! Many people have two love languages with very close scores (e.g., Quality Time and Physical Touch).'
      }
    ]
  },
  {
    id: 'love-language-compatibility',
    name: 'Love Language Compatibility',
    number: 6,
    phase: 4,
    phaseName: PHASE_NAMES[4],
    category: 'Love Languages',
    tagline: 'Compare two love languages and bridge emotional differences',
    description: 'Select your and your partner’s love languages to reveal your dynamic synergy, potential blind spots, and communication hacks.',
    detailedOverview: 'When partners speak different love languages, good intentions can be lost in translation. This tool decodes any combination of partner languages and offers actionable translation exercises.',
    howItWorks: [
      'Select Partner A’s primary & secondary love languages',
      'Select Partner B’s primary & secondary love languages',
      'Instantly generate your Love Language Synergy Matrix, common friction points, and daily micro-habits'
    ],
    whatResultMeans: 'A match is never "incompatible"—it simply requires knowing how to convert your natural affection style into gestures your partner truly feels.',
    icon: 'Repeat',
    isFunctional: true,
    badge: 'Trending',
    tags: ['love language match', 'couple translation', 'relationship communication'],
    faqs: [
      {
        question: 'What if we have completely opposite love languages?',
        answer: 'Opposite languages often make the strongest pairs because they expand how both partners experience affection once understood.'
      }
    ]
  },
  {
    id: 'how-well-do-you-know-your-partner',
    name: 'How Well Do You Know Your Partner?',
    number: 7,
    phase: 3,
    phaseName: PHASE_NAMES[3],
    category: 'Couple Quizzes',
    tagline: 'Test your partner trivia knowledge in a fun 10-question challenge',
    description: 'How well do you really know your partner’s favorite comforts, hidden pet peeves, dreams, and childhood memories?',
    detailedOverview: 'An engaging quiz game to test your mutual intimacy and detail retention. Play alone to test yourself, or take turns passing the device on date night!',
    howItWorks: [
      'Answer 10 fun trivia questions about your partner’s habits and preferences',
      'Calculate your score and review which answers show deep connection',
      'Receive a Partner IQ certificate title and hilarious conversation prompts'
    ],
    whatResultMeans: 'A fun gauge of emotional attunement. Any question you miss is simply a great excuse to ask your partner tonight!',
    icon: 'HelpCircle',
    isFunctional: true,
    badge: 'Popular',
    tags: ['partner quiz', 'trivia for couples', 'date night game', 'relationship test'],
    faqs: [
      {
        question: 'Can we play this as a 2-player date game?',
        answer: 'Absolutely! One person guesses while the other verifies, then swap roles.'
      }
    ]
  },
  {
    id: 'who-is-more-likely-couples',
    name: 'Who Is More Likely? — Couples',
    number: 8,
    phase: 5,
    phaseName: PHASE_NAMES[5],
    category: 'Couple Games',
    tagline: 'The ultimate couple card game with 35+ hilarious & sweet prompts',
    description: 'Vote on who takes longer to get ready, who apologizes first, who is more romantic, and who spends more money.',
    detailedOverview: 'Inspired by the viral couples challenge, this interactive tool brings 35+ curated prompts across Romantic, Funny, Daily Habits, and Deep categories. Vote in real-time and track who wins!',
    howItWorks: [
      'Pick a prompt or let the randomizer draw one',
      'Vote between You vs Partner (or Partner 1 vs Partner 2)',
      'Track live vote totals and spark playful laughter'
    ],
    whatResultMeans: 'A lighthearted game that highlights the charming quirks and dynamics that make your relationship uniquely yours.',
    icon: 'Gamepad2',
    isFunctional: true,
    badge: 'Popular',
    tags: ['who is more likely', 'couples game', 'date night cards', 'relationship icebreaker'],
    faqs: [
      {
        question: 'Can we filter by prompt category?',
        answer: 'Yes! Filter between Romantic, Funny, Daily Habits, and Deep prompts.'
      }
    ]
  },
  {
    id: 'relationship-duration-calculator',
    name: 'Relationship Duration Calculator',
    number: 9,
    phase: 6,
    phaseName: PHASE_NAMES[6],
    category: 'Calculators',
    tagline: 'Calculate exact years, months, days, hours, and seconds together',
    description: 'Enter your anniversary date to see a live-updating counter of your time together, upcoming milestones, and anniversary gift badges.',
    detailedOverview: 'Celebrate every moment of your journey together. This precision calculator breaks down your duration into total years, months, weeks, days, hours, minutes, and live ticking seconds.',
    howItWorks: [
      'Enter the date and optional time you started dating or got married',
      'The calculator tracks your exact duration with real-time second updates',
      'Unlock anniversary milestone badges (Paper, Wood, Tin, Silver, Golden) and countdown to your next anniversary'
    ],
    whatResultMeans: 'A beautiful visual tribute to the time and memories you have invested in each other.',
    icon: 'CalendarHeart',
    isFunctional: true,
    badge: 'Top Pick',
    tags: ['anniversary calculator', 'time together', 'relationship counter', 'milestone tracker'],
    faqs: [
      {
        question: 'Does the counter update in real-time?',
        answer: 'Yes, the seconds counter ticks live on screen while the page is open.'
      }
    ]
  },
  {
    id: 'days-together-calculator',
    name: 'Days Together Calculator',
    number: 10,
    phase: 6,
    phaseName: PHASE_NAMES[6],
    category: 'Calculators',
    tagline: 'Count total days together and unlock major relationship milestones',
    description: 'Discover your exact total day count, total heartbeats shared, sleep hours together, and 100, 500, and 1,000-day countdowns.',
    detailedOverview: 'Milestones like the 100th day, 1,000th day, or 5,000th day are trending relationship celebrations worldwide. Find out exactly where you stand and get reminder dates.',
    howItWorks: [
      'Select the date your journey began',
      'View your grand day total along with romantic stats (heartbeats, sunrises, hours together)',
      'See upcoming milestone countdowns to plan surprises'
    ],
    whatResultMeans: 'Every day together is an accomplishment. Use this tool to plan special milestone dates that ordinary calendars miss!',
    icon: 'Clock',
    isFunctional: true,
    badge: 'Popular',
    tags: ['days together', '100 days anniversary', 'day counter', 'couples counter'],
    faqs: [
      {
        question: 'Why celebrate the 100th or 1000th day?',
        answer: 'Day milestones offer fresh, unexpected opportunities to celebrate romance beyond standard yearly anniversaries.'
      }
    ]
  },

  // ==========================================
  // PHASE 1 CONTINUED (Tools 11 - 16)
  // ==========================================
  {
    id: 'crush-compatibility-meter',
    name: 'Crush Compatibility Meter',
    number: 11,
    phase: 1,
    phaseName: PHASE_NAMES[1],
    category: 'Love Tests',
    tagline: 'Gauge your secret crush vibe and romantic potential',
    description: 'Enter your names and crush clues to analyze secret signals and calculate your crush match score.',
    detailedOverview: 'Wondering if that special someone shares your feelings? This playful tool combines name harmony with behavioral crush signals for a fun assessment.',
    howItWorks: ['Enter names', 'Answer 3 quick vibe clues', 'Get your Crush Chemistry Score'],
    whatResultMeans: 'A playful confidence booster to help you make your next move with a smile.',
    icon: 'Flame',
    isFunctional: true,
    tags: ['crush test', 'secret crush', 'vibe check'],
    faqs: [{ question: 'Is this accurate?', answer: 'For entertainment and encouragement only!' }]
  },
  {
    id: 'flames-love-game',
    name: 'FLAMES Love Calculator',
    number: 12,
    phase: 1,
    phaseName: PHASE_NAMES[1],
    category: 'Love Tests',
    tagline: 'The classic childhood paper game: Friends, Lovers, Affection, Marriage, Enemies, Siblings',
    description: 'Relive the nostalgic FLAMES game with automated letter cancellation and outcome analysis.',
    detailedOverview: 'The classic game loved across generations worldwide. Removes matching letters from both names to count and land on F, L, A, M, E, or S.',
    howItWorks: ['Input two full names', 'Cancel common letters', 'Tally the FLAMES cycle outcome'],
    whatResultMeans: 'Reveals whether the classic formula predicts Romance, Friendship, or Marriage!',
    icon: 'Sparkle',
    isFunctional: true,
    badge: 'Classic',
    tags: ['flames game', 'childhood love test', 'nostalgia'],
    faqs: [{ question: 'What does FLAMES stand for?', answer: 'Friends, Lovers, Affection, Marriage, Enemies, Siblings.' }]
  },
  {
    id: 'zodiac-love-calculator',
    name: 'Zodiac Love Calculator',
    number: 13,
    phase: 1,
    phaseName: PHASE_NAMES[1],
    category: 'Love Tests',
    tagline: 'Astrological elemental affinity between your two star signs',
    description: 'Calculate sun sign compatibility between Fire, Earth, Air, and Water combinations.',
    detailedOverview: 'Combines astrological element theory with traditional zodiac polarity to calculate your celestial romance percentage.',
    howItWorks: ['Select your sign', 'Select partner sign', 'Discover elemental harmony and friction points'],
    whatResultMeans: 'Insights into how Fire warms Air, Water enriches Earth, and how opposites attract.',
    icon: 'Moon',
    isFunctional: true,
    tags: ['zodiac love', 'horoscope match', 'astrology compatibility'],
    faqs: [{ question: 'Which elements match best?', answer: 'Fire & Air, and Earth & Water traditionally have high natural synergy.' }]
  },
  {
    id: 'birthday-love-calculator',
    name: 'Birthday Love Calculator',
    number: 14,
    phase: 1,
    phaseName: PHASE_NAMES[1],
    category: 'Love Tests',
    tagline: 'Numerology life path match based on birthdates',
    description: 'Reduce birthdates to root numerology life path numbers to reveal spiritual affinity.',
    detailedOverview: 'Numerology studies how vibrational digits shape our personalities and magnetic attraction toward partners.',
    howItWorks: ['Enter birthdate A and B', 'Calculate Life Path numbers', 'Examine numerological harmony'],
    whatResultMeans: 'Learn how your life paths complement and teach each other life lessons.',
    icon: 'Calendar',
    isFunctional: true,
    tags: ['birthday match', 'numerology love', 'life path'],
    faqs: [{ question: 'What is a life path number?', answer: 'The sum of your birth year, month, and day reduced to a single digit or master number.' }]
  },
  {
    id: 'initials-love-matcher',
    name: 'Initials Love Matcher',
    number: 15,
    phase: 1,
    phaseName: PHASE_NAMES[1],
    category: 'Love Tests',
    tagline: 'Quick monogram romantic affinity calculator',
    description: 'Carve your initials into the digital tree trunk to compute instant monogram chemistry.',
    detailedOverview: 'A simple, sweet visual tool celebrating the classic tradition of carving two initials inside a heart.',
    howItWorks: ['Type two initials', 'Watch the heart carve animation', 'Receive your monogram score'],
    whatResultMeans: 'A joyful micro-tool designed for fast sharing on social media stories.',
    icon: 'Heart',
    isFunctional: true,
    tags: ['initials match', 'monogram love', 'fast test'],
    faqs: [{ question: 'Can I share the monogram badge?', answer: 'Yes, easily downloadable and shareable.' }]
  },
  {
    id: 'true-love-potential-score',
    name: 'True Love Potential Score',
    number: 16,
    phase: 1,
    phaseName: PHASE_NAMES[1],
    category: 'Love Tests',
    tagline: 'Holistic romantic potential index across 4 relationship pillars',
    description: 'Blends name vibration, attraction style, and emotional expectations into a single True Love Score.',
    detailedOverview: 'A hybrid calculator that factors in both aesthetic resonance and personal intentions for modern dating.',
    howItWorks: ['Answer 4 quick lifestyle prompts', 'Enter partner details', 'Receive comprehensive index'],
    whatResultMeans: 'Reflects current romantic readiness and partner compatibility.',
    icon: 'ShieldCheck',
    isFunctional: true,
    tags: ['true love', 'potential score', 'relationship gauge'],
    faqs: [{ question: 'What is the highest score?', answer: '100% represents optimal synergy across all 4 pillars.' }]
  },

  // ==========================================
  // PHASE 2: COMPATIBILITY TESTS (17 - 33)
  // ==========================================
  {
    id: 'emotional-compatibility-test',
    name: 'Emotional Compatibility Test',
    number: 17,
    phase: 2,
    phaseName: PHASE_NAMES[2],
    category: 'Compatibility',
    tagline: 'Measure vulnerability, empathy, and emotional safety between partners',
    description: 'Do you feel heard and comforted when life gets hard? Assess your emotional resonance.',
    detailedOverview: 'Deep emotional intimacy is the single greatest predictor of relationship longevity.',
    howItWorks: ['Rate responses to emotional scenarios', 'Compute vulnerability balance', 'Get empathy tips'],
    whatResultMeans: 'Identifies whether you connect as emotional anchors or need to foster deeper emotional safety.',
    icon: 'HeartHandshake',
    isFunctional: true,
    tags: ['emotional intimacy', 'empathy', 'vulnerability'],
    faqs: [{ question: 'What is emotional safety?', answer: 'Knowing your feelings will be validated without judgment or fear of retaliation.' }]
  },
  {
    id: 'communication-style-match',
    name: 'Communication Style Match',
    number: 18,
    phase: 2,
    phaseName: PHASE_NAMES[2],
    category: 'Compatibility',
    tagline: 'Identify your conversational styles and prevent misunderstandings',
    description: 'Are you a direct communicator, a processor, a gentle diplomat, or a verbal debater?',
    detailedOverview: 'Most relationship fights are communication breakdowns, not lack of love. Learn your style combination.',
    howItWorks: ['Assess discussion habits', 'Map partner dynamics', 'Receive a communication cheat sheet'],
    whatResultMeans: 'Provides actionable adjustments for calm, loving discussions during stress.',
    icon: 'MessageSquare',
    isFunctional: true,
    tags: ['communication', 'arguments', 'couple talks'],
    faqs: [{ question: 'Can different communication styles work?', answer: 'Yes, with simple ground rules like honoring cooldown times.' }]
  },
  {
    id: 'lifestyle-habits-compatibility',
    name: 'Lifestyle & Habits Compatibility',
    number: 19,
    phase: 2,
    phaseName: PHASE_NAMES[2],
    category: 'Compatibility',
    tagline: 'Compare cleanliness, sleep schedules, and daily routines',
    description: 'Morning bird vs night owl? Minimalist vs collector? Test how well your daily routines align.',
    detailedOverview: 'Great romance survives big problems, but daily friction over routines can wear down love.',
    howItWorks: ['Compare sleep, cooking, chore, and social habits', 'Calculate lifestyle friction index'],
    whatResultMeans: 'Highlights where compromise and clear boundaries will save domestic harmony.',
    icon: 'Home',
    isFunctional: true,
    tags: ['living together', 'moving in', 'habits match'],
    faqs: [{ question: 'Should couples share identical routines?', answer: 'No, respecting each other’s rhythms is what matters.' }]
  },
  {
    id: 'financial-harmony-quiz',
    name: 'Financial Harmony Quiz',
    number: 20,
    phase: 2,
    phaseName: PHASE_NAMES[2],
    category: 'Compatibility',
    tagline: 'Saver vs Spender? Evaluate your money mindset compatibility',
    description: 'Money is a top topic in partnerships. Explore how you navigate budgets, savings, and splurges.',
    detailedOverview: 'A compassionate, non-judgmental look at financial values, goals, and transparency.',
    howItWorks: ['Answer 6 financial dilemma questions', 'Score your financial compatibility band'],
    whatResultMeans: 'Helps partners discuss finances comfortably before tension arises.',
    icon: 'Coins',
    isFunctional: true,
    tags: ['money quiz', 'financial goals', 'budget together'],
    faqs: [{ question: 'Is financial disagreement common?', answer: 'Very common, and completely manageable with open monthly money dates.' }]
  },
  {
    id: 'future-goals-vision-match',
    name: 'Future Goals & Vision Match',
    number: 21,
    phase: 2,
    phaseName: PHASE_NAMES[2],
    category: 'Compatibility',
    tagline: 'Align on career, family, travel, and location dreams',
    description: 'Are you heading in the same direction? Test your 5-year and 10-year life vision compatibility.',
    detailedOverview: 'Explores long-term alignment across career ambitions, children, retirement, and geography.',
    howItWorks: ['Answer vision milestone questions', 'Identify shared paths and fork points'],
    whatResultMeans: 'Clarifies whether you are building a unified future together.',
    icon: 'Compass',
    isFunctional: true,
    tags: ['future plans', 'marriage goals', 'life vision'],
    faqs: [{ question: 'What if our careers pull in different directions?', answer: 'Healthy partners negotiate alternating priorities and creative compromises.' }]
  },
  {
    id: 'introvert-extrovert-couple-match',
    name: 'Introvert-Extrovert Couple Match',
    number: 22,
    phase: 2,
    phaseName: PHASE_NAMES[2],
    category: 'Compatibility',
    tagline: 'Balance social energy, alone time, and weekend plans',
    description: 'Discover the superpower and survival tips when an introvert and extrovert fall in love.',
    detailedOverview: 'Introvert-extrovert pairs can balance each other beautifully once social batteries are respected.',
    howItWorks: ['Rate social battery recharge preferences', 'Receive custom date night balance tips'],
    whatResultMeans: 'Provides actionable rules for party limits and cozy recharge weekends.',
    icon: 'BatteryCharging',
    isFunctional: true,
    tags: ['introvert extrovert', 'social battery', 'energy match'],
    faqs: [{ question: 'Why do introverts and extroverts attract?', answer: 'They provide the grounding or adventure the other naturally craves.' }]
  },
  {
    id: 'humor-and-banter-compatibility',
    name: 'Humor & Banter Compatibility',
    number: 23,
    phase: 2,
    phaseName: PHASE_NAMES[2],
    category: 'Compatibility',
    tagline: 'Do you laugh at the same memes, jokes, and life situations?',
    description: 'Laughter bonds couples through hard times. Test your humor wavelength alignment.',
    detailedOverview: 'Assesses sarcasm, slapstick, dry wit, and silliness compatibility.',
    howItWorks: ['Pick reactions to funny scenarios', 'Calculate your mutual Comedy Rating'],
    whatResultMeans: 'Identifies whether your humor brings you closer and diffuses tension.',
    icon: 'Smile',
    isFunctional: true,
    tags: ['humor test', 'banter match', 'laughter quiz'],
    faqs: [{ question: 'Is shared humor necessary?', answer: 'Shared humor is one of the highest correlated factors in couple satisfaction!' }]
  },
  {
    id: 'conflict-resolution-style-test',
    name: 'Conflict Resolution Style Test',
    number: 24,
    phase: 2,
    phaseName: PHASE_NAMES[2],
    category: 'Compatibility',
    tagline: 'How do you fight, cool down, and make up after disagreements?',
    description: 'Identify if you are a Problem-Solver, Calmer, Ventor, or Conflict-Avoider.',
    detailedOverview: 'All couples fight; healthy couples fight fair. Learn your repair attempt triggers.',
    howItWorks: ['Answer how you react during arguments', 'Discover your repair strategy archetype'],
    whatResultMeans: 'Offers concrete phrases to de-escalate fights in under 5 minutes.',
    icon: 'Scale',
    isFunctional: true,
    tags: ['conflict resolution', 'fair fighting', 'arguments'],
    faqs: [{ question: 'What is a repair attempt?', answer: 'A gesture or joke used to stop negative escalation during a disagreement.' }]
  },
  {
    id: 'attachment-style-compatibility',
    name: 'Attachment Style Compatibility',
    number: 25,
    phase: 2,
    phaseName: PHASE_NAMES[2],
    category: 'Compatibility',
    tagline: 'Secure, Anxious, Avoidant, or Fearful? Unpack your bond dynamics',
    description: 'Understand how childhood attachment patterns influence your adult romantic triggers.',
    detailedOverview: 'Grounded in psychological attachment theory to help couples break anxious-avoidant traps.',
    howItWorks: ['Select intimacy and independence comfort levels', 'Get attachment synergy profile'],
    whatResultMeans: 'Teaches how to cultivate "earned security" together.',
    icon: 'Shield',
    isFunctional: true,
    badge: 'Popular',
    tags: ['attachment style', 'secure love', 'anxious avoidant'],
    faqs: [{ question: 'Can attachment styles change?', answer: 'Yes, with consistency and self-awareness, people develop secure attachment.' }]
  },
  {
    id: 'intellectual-compatibility-test',
    name: 'Intellectual Compatibility Test',
    number: 26,
    phase: 2,
    phaseName: PHASE_NAMES[2],
    category: 'Compatibility',
    tagline: 'Deep conversations, curiosity, and mental spark test',
    description: 'Do you challenge each other mentally and love talking late into the night?',
    detailedOverview: 'Tests curiosity match, debate enjoyment, and mutual respect for intellect.',
    howItWorks: ['Rate intellectual interests', 'Measure curiosity and conversational flow'],
    whatResultMeans: 'Highlights how your minds spark creative and inspiring ideas together.',
    icon: 'Brain',
    isFunctional: true,
    tags: ['mental spark', 'deep talk', 'curiosity'],
    faqs: [{ question: 'Do partners need the same degree?', answer: 'Not at all; intellectual compatibility is about mutual curiosity, not credentials.' }]
  },
  {
    id: 'adventure-vs-comfort-meter',
    name: 'Adventure vs Comfort Meter',
    number: 27,
    phase: 2,
    phaseName: PHASE_NAMES[2],
    category: 'Compatibility',
    tagline: 'Spontaneous road trips or cozy movie nights at home?',
    description: 'Calculate your couple balance between thrill-seeking adventures and relaxing homebody peace.',
    detailedOverview: 'Helps balance adrenaline and rest so neither partner feels exhausted or bored.',
    howItWorks: ['Choose weekend and holiday preferences', 'Get your Adventure-to-Nest ratio'],
    whatResultMeans: 'Finds the sweet spot between exploration and recharging.',
    icon: 'Plane',
    isFunctional: true,
    tags: ['vacation style', 'adventures', 'homebody'],
    faqs: [{ question: 'How to plan vacations when styles differ?', answer: 'Alternate days: one adventure day followed by one lounging pool day.' }]
  },
  {
    id: 'family-values-compatibility',
    name: 'Family Values Compatibility',
    number: 28,
    phase: 2,
    phaseName: PHASE_NAMES[2],
    category: 'Compatibility',
    tagline: 'Traditions, in-law dynamics, parenting thoughts, and holidays',
    description: 'Explore how your family background shapes your expectations for your own home.',
    detailedOverview: 'Addresses family involvement, holiday hosting, and generational boundary settings.',
    howItWorks: ['Answer family priority questions', 'Analyze tradition harmony'],
    whatResultMeans: 'Helps establish strong couple unity when engaging with extended families.',
    icon: 'Users2',
    isFunctional: true,
    tags: ['in-laws', 'family values', 'traditions'],
    faqs: [{ question: 'How do we handle holiday balance?', answer: 'Set a rotating schedule early in the year to prevent holiday stress.' }]
  },
  {
    id: 'physical-affection-frequency-match',
    name: 'Physical Affection Frequency Match',
    number: 29,
    phase: 2,
    phaseName: PHASE_NAMES[2],
    category: 'Compatibility',
    tagline: 'Hugs, kisses, hand-holding, and touch rhythm compatibility',
    description: 'Compare non-verbal affection expectations and cuddle comfort levels.',
    detailedOverview: 'Examines affectionate touch, holding hands in public, and physical reassurance.',
    howItWorks: ['Indicate touch preferences across scenarios', 'Receive touch attunement score'],
    whatResultMeans: 'Bridges differences between high-touch and low-touch partners gently.',
    icon: 'Sparkles',
    isFunctional: true,
    tags: ['affection', 'touch', 'cuddling'],
    faqs: [{ question: 'What if one partner needs more personal space?', answer: 'Scheduled gentle moments (like 5-minute morning hugs) bridge the gap.' }]
  },
  {
    id: 'spontaneity-vs-planning-match',
    name: 'Spontaneity vs Planning Match',
    number: 30,
    phase: 2,
    phaseName: PHASE_NAMES[2],
    category: 'Compatibility',
    tagline: 'Spreadsheets vs winging it: discover your couple planning vibe',
    description: 'Find out how to thrive when one partner loves agendas and the other loves impulse.',
    detailedOverview: 'Celebrates the planner who keeps you on track and the spontaneous spirit who brings surprises.',
    howItWorks: ['Rate preparation habits', 'Get the ideal Planning-Spontaneity formula'],
    whatResultMeans: 'Prevents vacation arguments and daily schedule misunderstandings.',
    icon: 'ListChecks',
    isFunctional: true,
    tags: ['planner', 'spontaneous', 'trip planning'],
    faqs: [{ question: 'Can opposites thrive here?', answer: 'They make the best travel companions when roles are appreciated!' }]
  },
  {
    id: 'career-ambition-synergy',
    name: 'Career & Ambition Synergy',
    number: 31,
    phase: 2,
    phaseName: PHASE_NAMES[2],
    category: 'Compatibility',
    tagline: 'Supporting each other’s professional dreams and work-life balance',
    description: 'Evaluate how your career stages, hours, and ambitions support one another.',
    detailedOverview: 'Ensures neither partner feels sidelined by work commitments or dual careers.',
    howItWorks: ['Answer ambition questions', 'Compute professional support score'],
    whatResultMeans: 'Fosters a power-couple mindset with healthy boundaries.',
    icon: 'Briefcase',
    isFunctional: true,
    tags: ['career goals', 'work life balance', 'power couple'],
    faqs: [{ question: 'How to handle late working hours?', answer: 'Protect 30 minutes of undivided connection before bedtime every night.' }]
  },
  {
    id: 'spiritual-and-philosophical-match',
    name: 'Spiritual & Philosophical Match',
    number: 32,
    phase: 2,
    phaseName: PHASE_NAMES[2],
    category: 'Compatibility',
    tagline: 'Shared morals, ethics, spirituality, and life philosophy',
    description: 'Explore the deeper beliefs that guide your choices and worldviews.',
    detailedOverview: 'Assesses mutual respect for spiritual traditions, existential views, and ethics.',
    howItWorks: ['Respond to deep values prompts', 'Receive philosophical harmony index'],
    whatResultMeans: 'Highlights universal human values that bind you beyond labels.',
    icon: 'SunMedium',
    isFunctional: true,
    tags: ['spirituality', 'philosophy', 'ethics'],
    faqs: [{ question: 'Can interfaith couples work?', answer: 'Yes! Open curiosity and celebrating both traditions creates a rich home.' }]
  },
  {
    id: 'roommate-compatibility-test',
    name: 'Roommate & Co-Living Test',
    number: 33,
    phase: 2,
    phaseName: PHASE_NAMES[2],
    category: 'Compatibility',
    tagline: 'Are you ready to move in together? The co-living compatibility test',
    description: 'Test chore distribution, thermostat wars, bathroom sharing, and guest policies.',
    detailedOverview: 'Crucial checklist before signing a lease or moving in with your partner.',
    howItWorks: ['Answer 8 domestic scenarios', 'Calculate your Co-Living Readiness Score'],
    whatResultMeans: 'Provides a ready-to-use chore and quiet-hours roommate agreement template.',
    icon: 'KeyRound',
    isFunctional: true,
    badge: 'Popular',
    tags: ['moving in', 'roommate test', 'co living'],
    faqs: [{ question: 'When is the right time to move in?', answer: 'When you can discuss finances and household chores comfortably and honestly.' }]
  },

  // ==========================================
  // PHASE 3: COUPLE QUIZZES (34 - 50)
  // ==========================================
  {
    id: 'relationship-health-checkup',
    name: 'Relationship Health Checkup',
    number: 34,
    phase: 3,
    phaseName: PHASE_NAMES[3],
    category: 'Couple Quizzes',
    tagline: 'A comprehensive wellness checkup for your relationship vitals',
    description: 'Evaluate trust, appreciation, intimacy, and communication health.',
    detailedOverview: 'Think of this as an annual checkup for your love life to catch small issues early.',
    howItWorks: ['Review 7 wellness markers', 'Get your Relationship Health Score'],
    whatResultMeans: 'Points out healthy vitals and recommends simple booster activities.',
    icon: 'Activity',
    isFunctional: true,
    tags: ['health check', 'marriage checkup', 'relationship audit'],
    faqs: [{ question: 'How often should we do a checkup?', answer: 'Every 3 to 6 months keeps connection fresh.' }]
  },
  {
    id: 'green-flags-vs-red-flags-quiz',
    name: 'Green Flags vs Red Flags Quiz',
    number: 35,
    phase: 3,
    phaseName: PHASE_NAMES[3],
    category: 'Couple Quizzes',
    tagline: 'Count the healthy green flags and spot subtle warning signals',
    description: 'Is your relationship full of healthy green flags? Tally your relationship green score.',
    detailedOverview: 'Celebrates emotional maturity, accountability, and kindness while noting concerns.',
    howItWorks: ['Check off relationship dynamics', 'Calculate your Green Flag Percentage'],
    whatResultMeans: 'Affirms whether your bond is built on solid, trustworthy foundations.',
    icon: 'Flag',
    isFunctional: true,
    badge: 'Popular',
    tags: ['green flags', 'red flags', 'healthy relationship'],
    faqs: [{ question: 'What is the most important green flag?', answer: 'Consistency between someone’s words and actions over time.' }]
  },
  {
    id: 'dating-stage-milestone-quiz',
    name: 'Dating Stage & Milestone Quiz',
    number: 36,
    phase: 3,
    phaseName: PHASE_NAMES[3],
    category: 'Couple Quizzes',
    tagline: 'Where do you stand? Talking stage, official, or ready for forever?',
    description: 'Clarify your dating status and test readiness for the next milestone.',
    detailedOverview: 'Helps couples transition from the "talking stage" to official, exclusivity, or engagement.',
    howItWorks: ['Answer relationship benchmark questions', 'Receive your current Relationship Phase'],
    whatResultMeans: 'Guides open conversation on when to take your next big step.',
    icon: 'Milestone',
    isFunctional: true,
    tags: ['talking stage', 'making it official', 'dating milestones'],
    faqs: [{ question: 'When should we have "the talk"?', answer: 'When you want exclusivity and want clarity on shared commitment.' }]
  },
  {
    id: 'spark-and-romance-meter',
    name: 'Spark & Romance Meter',
    number: 37,
    phase: 3,
    phaseName: PHASE_NAMES[3],
    category: 'Couple Quizzes',
    tagline: 'How bright is your romantic spark burning right now?',
    description: 'Gauge flirtation, date-night novelty, and butterflies in your current routine.',
    detailedOverview: 'Long-term love doesn’t mean losing the spark. Measure your novelty and passion index.',
    howItWorks: ['Rate recent romantic gestures', 'Get custom date night spark ideas'],
    whatResultMeans: 'Recommends 3 creative surprise ideas to rekindle playful romance.',
    icon: 'Zap',
    isFunctional: true,
    tags: ['the spark', 'romance quiz', 'rekindle love'],
    faqs: [{ question: 'Is it normal for the initial spark to shift?', answer: 'Yes! The frantic dopamine spark naturally evolves into comforting deep warmth.' }]
  },
  {
    id: 'couple-trust-score-test',
    name: 'Couple Trust Score Test',
    number: 38,
    phase: 3,
    phaseName: PHASE_NAMES[3],
    category: 'Couple Quizzes',
    tagline: 'Evaluate honesty, emotional reliability, and transparency',
    description: 'Trust is the bedrock of love. Tally your trust quotient across 6 core pillars.',
    detailedOverview: 'Measures predictability, confidentiality, and emotional follow-through.',
    howItWorks: ['Rate trust statements', 'Calculate your Couple Trust Index'],
    whatResultMeans: 'Validates feelings of emotional security and dependability.',
    icon: 'ShieldAlert',
    isFunctional: true,
    tags: ['trust test', 'honesty', 'faithfulness'],
    faqs: [{ question: 'How is trust rebuilt?', answer: 'Through sustained consistency, radical transparency, and patience over time.' }]
  },
  {
    id: 'appreciation-gratitude-quiz',
    name: 'Appreciation & Gratitude Quiz',
    number: 39,
    phase: 3,
    phaseName: PHASE_NAMES[3],
    category: 'Couple Quizzes',
    tagline: 'How valued does your partner feel day-to-day?',
    description: 'Test the frequency and sincerity of "thank you"s, compliments, and recognition.',
    detailedOverview: 'Feeling taken for granted is a silent killer of romance. Test your gratitude habits.',
    howItWorks: ['Assess weekly appreciation exchanges', 'Get appreciation booster suggestions'],
    whatResultMeans: 'Teaches simple 30-second daily appreciation habits that transform connection.',
    icon: 'HeartHandshake',
    isFunctional: true,
    tags: ['gratitude', 'feeling appreciated', 'compliments'],
    faqs: [{ question: 'What is a 1-minute gratitude habit?', answer: 'Telling your partner one specific thing you admired about them before sleep.' }]
  },
  {
    id: 'dating-readiness-quiz',
    name: 'Dating Readiness Quiz',
    number: 40,
    phase: 3,
    phaseName: PHASE_NAMES[3],
    category: 'Couple Quizzes',
    tagline: 'Are you emotionally ready to start dating again?',
    description: 'Check your heart readiness, healing from the past, and self-love foundation.',
    detailedOverview: 'A compassionate tool for singles looking to enter the dating world with confidence.',
    howItWorks: ['Evaluate past relationship closure and self-worth', 'Get readiness feedback'],
    whatResultMeans: 'Shows whether you are dating from abundance or loneliness.',
    icon: 'UserCheck',
    isFunctional: true,
    tags: ['ready to date', 'single quiz', 'healing after breakup'],
    faqs: [{ question: 'How do I know I am over my ex?', answer: 'When thoughts of them bring neutral acceptance rather than intense emotion.' }]
  },
  {
    id: 'long-distance-relationship-strength',
    name: 'Long Distance Relationship (LDR) Strength',
    number: 41,
    phase: 3,
    phaseName: PHASE_NAMES[3],
    category: 'Couple Quizzes',
    tagline: 'Test your communication, countdown plan, and LDR resilience',
    description: 'Long distance takes grit and creativity. Gauge your LDR survival index.',
    detailedOverview: 'Assesses digital date routines, visits, mutual trust, and closing-the-distance plans.',
    howItWorks: ['Rate virtual connection habits', 'Calculate LDR Longevity Score'],
    whatResultMeans: 'Provides virtual date ideas and tips to keep intimacy alive across timezones.',
    icon: 'Globe',
    isFunctional: true,
    badge: 'Trending',
    tags: ['long distance', 'ldr quiz', 'virtual date'],
    faqs: [{ question: 'What is the golden rule of LDR?', answer: 'Always have the exact date of your next in-person visit planned.' }]
  },
  {
    id: 'are-you-soulmates-quiz',
    name: 'Are You Soulmates Quiz',
    number: 42,
    phase: 3,
    phaseName: PHASE_NAMES[3],
    category: 'Couple Quizzes',
    tagline: 'Explore the feeling of destiny, synchronicities, and deep soul resonance',
    description: 'Did you know instantly? Test your soul connection and spiritual bond.',
    detailedOverview: 'Combines romantic serendipity with deep psychological attunement.',
    howItWorks: ['Answer intuitive questions about your first meeting and bond', 'Receive Soulmate %'],
    whatResultMeans: 'A magical celebration of meeting the right person at the right time.',
    icon: 'Sparkles',
    isFunctional: true,
    tags: ['soulmates', 'destiny quiz', 'twin flame'],
    faqs: [{ question: 'Do soulmates fight?', answer: 'Yes! Soulmates are mirrors who challenge us to grow into our highest selves.' }]
  },
  {
    id: 'marriage-readiness-assessment',
    name: 'Marriage Readiness Assessment',
    number: 43,
    phase: 3,
    phaseName: PHASE_NAMES[3],
    category: 'Couple Quizzes',
    tagline: 'Are you ready to say "I Do"? Crucial pre-engagement questions',
    description: 'Explore shared life philosophies, crisis management, and lifelong commitment readiness.',
    detailedOverview: 'Based on pre-marital counseling frameworks to ensure you’re ready for marriage, not just a wedding.',
    howItWorks: ['Review 10 pre-marital pillars', 'Receive Marriage Readiness Report'],
    whatResultMeans: 'Highlights topics to discuss before buying a ring or booking a venue.',
    icon: 'Gem',
    isFunctional: true,
    tags: ['marriage ready', 'getting engaged', 'pre marital'],
    faqs: [{ question: 'What should couples agree on before marriage?', answer: 'Finances, children, career balance, in-law boundaries, and conflict resolution.' }]
  },
  {
    id: 'jealousy-and-security-quiz',
    name: 'Jealousy & Security Quiz',
    number: 44,
    phase: 3,
    phaseName: PHASE_NAMES[3],
    category: 'Couple Quizzes',
    tagline: 'Examine jealousy triggers and strengthen mutual reassurance',
    description: 'Understand the root causes of relationship insecurity and learn healthy reassurance habits.',
    detailedOverview: 'Deconstructs jealousy into fear of loss or lack of communication, offering gentle healing steps.',
    howItWorks: ['Identify social and trust reactions', 'Get security calibration advice'],
    whatResultMeans: 'Distinguishes between protective love and restrictive anxiety.',
    icon: 'HeartOff',
    isFunctional: true,
    tags: ['jealousy', 'insecurity', 'trust building'],
    faqs: [{ question: 'Is a little jealousy healthy?', answer: 'Mild jealousy is natural; how you communicate it determines if it strengthens or damages trust.' }]
  },
  {
    id: 'couple-stress-handling-quiz',
    name: 'Couple Stress-Handling Quiz',
    number: 45,
    phase: 3,
    phaseName: PHASE_NAMES[3],
    category: 'Couple Quizzes',
    tagline: 'How do you support each other when external life gets crazy?',
    description: 'Do you take stress out on each other or team up against the problem?',
    detailedOverview: 'Life brings job stress, family drama, and illness. Test your team-resilience under pressure.',
    howItWorks: ['Evaluate stress responses', 'Score your Team-Under-Pressure Rating'],
    whatResultMeans: 'Teaches the "It’s You & Me vs The Problem" mindset.',
    icon: 'Umbrella',
    isFunctional: true,
    tags: ['stress together', 'burnout', 'coping as a couple'],
    faqs: [{ question: 'How to comfort a stressed partner?', answer: 'Ask: "Do you want comfort, advice, or a distraction right now?"' }]
  },
  {
    id: 'parenting-vision-alignment',
    name: 'Parenting Vision Alignment',
    number: 46,
    phase: 3,
    phaseName: PHASE_NAMES[3],
    category: 'Couple Quizzes',
    tagline: 'Discipline styles, education thoughts, and raising kids together',
    description: 'Compare parenting philosophies before or during raising a family.',
    detailedOverview: 'Covers discipline, screen time, schooling, routines, and modeling healthy love.',
    howItWorks: ['Compare child-rearing approaches', 'Discover your parenting synergy'],
    whatResultMeans: 'Prevents the "good cop / bad cop" dynamic by presenting a united front.',
    icon: 'Baby',
    isFunctional: true,
    tags: ['parenting quiz', 'kids', 'family planning'],
    faqs: [{ question: 'What if we disagree on discipline?', answer: 'Always discuss rules privately and never contradict your partner in front of children.' }]
  },
  {
    id: 'forgiveness-and-letting-go-test',
    name: 'Forgiveness & Letting Go Test',
    number: 47,
    phase: 3,
    phaseName: PHASE_NAMES[3],
    category: 'Couple Quizzes',
    tagline: 'Do you hold grudges or practice genuine emotional repair?',
    description: 'Test your capacity to forgive past mistakes and move forward together with a clean slate.',
    detailedOverview: 'A relationship cannot survive scorekeeping. Assess your forgiveness resilience.',
    howItWorks: ['Answer past grievance prompts', 'Receive your Forgiveness Quotient'],
    whatResultMeans: 'Helps partners retire old arguments and heal lingering resentments.',
    icon: 'Unlock',
    isFunctional: true,
    tags: ['forgiveness', 'letting go', 'stop arguing'],
    faqs: [{ question: 'Does forgiveness mean forgetting?', answer: 'No, forgiveness means releasing the right to punish your partner for past resolved errors.' }]
  },
  {
    id: 'couple-generosity-meter',
    name: 'Couple Generosity Meter',
    number: 48,
    phase: 3,
    phaseName: PHASE_NAMES[3],
    category: 'Couple Quizzes',
    tagline: 'Measuring kindness, thoughtful surprises, and giving spirit',
    description: 'How often do you go out of your way to make your partner’s life easier or sweeter?',
    detailedOverview: 'Small daily acts of kindness fuel the emotional bank account faster than grand gestures.',
    howItWorks: ['Tally weekly acts of thoughtfulness', 'Score your Generosity Balance'],
    whatResultMeans: 'Unlocks actionable ideas to surprise your partner this week.',
    icon: 'Gift',
    isFunctional: true,
    tags: ['kindness', 'generosity', 'thoughtful gestures'],
    faqs: [{ question: 'What is a micro-gesture?', answer: 'Bringing their coffee in the morning or warming their towel on a cold day.' }]
  },
  {
    id: 'intimacy-depth-quiz',
    name: 'Intimacy Depth Quiz',
    number: 49,
    phase: 3,
    phaseName: PHASE_NAMES[3],
    category: 'Couple Quizzes',
    tagline: 'Explore emotional, intellectual, experiential, and physical closeness',
    description: 'Intimacy has four interconnected chambers. Measure your closeness across all four.',
    detailedOverview: 'Provides a holistic snapshot of where your connection is thriving and where it can deepen.',
    howItWorks: ['Review four dimensions of intimacy', 'Receive comprehensive Intimacy Map'],
    whatResultMeans: 'Celebrates your strongest intimacy pillar while giving fun prompts for the others.',
    icon: 'Flame',
    isFunctional: true,
    tags: ['intimacy', 'closeness', 'deep connection'],
    faqs: [{ question: 'What is experiential intimacy?', answer: 'Bonding through shared activities, challenges, projects, and travel together.' }]
  },
  {
    id: 'relationship-boredom-buster',
    name: 'Relationship Boredom Buster Quiz',
    number: 50,
    phase: 3,
    phaseName: PHASE_NAMES[3],
    category: 'Couple Quizzes',
    tagline: 'Shake up your routine and inject spontaneous adventure',
    description: 'Stuck in a Netflix-and-couch loop? Diagnose your routine and get a customized spice-up list.',
    detailedOverview: 'Routine is comfortable, but novelty triggers dopamine. Rediscover that exciting first-month rush.',
    howItWorks: ['Answer questions about your weekend habits', 'Receive custom adventure prescriptions'],
    whatResultMeans: 'Delivers 5 unconventional date ideas tailored to your budget and vibe.',
    icon: 'Sparkles',
    isFunctional: true,
    tags: ['date night ideas', 'break the routine', 'boredom buster'],
    faqs: [{ question: 'How much novelty is needed?', answer: 'Just one novel experience every two weeks keeps romance vibrant.' }]
  },

  // ==========================================
  // PHASE 4: LOVE LANGUAGES & PERSONALITY (51 - 67)
  // ==========================================
  {
    id: 'apology-language-test',
    name: 'Apology Language Test',
    number: 51,
    phase: 4,
    phaseName: PHASE_NAMES[4],
    category: 'Love Languages',
    tagline: 'Expressing Regret, Accepting Responsibility, Making Restitution, Repenting, or Requesting Forgiveness?',
    description: 'Saying "I am sorry" isn’t always enough. Learn your primary apology language.',
    detailedOverview: 'Based on Chapman & Thomas’s research on how different people hear and accept sincere apologies.',
    howItWorks: ['Rate apology scenarios', 'Discover your primary apology style'],
    whatResultMeans: 'Ensures your apologies heal hurts instead of sparking secondary arguments.',
    icon: 'MessageSquareHeart',
    isFunctional: true,
    badge: 'Top Pick',
    tags: ['apology language', 'saying sorry', 'relationship repair'],
    faqs: [{ question: 'What are the 5 apology languages?', answer: 'Expressing regret, accepting responsibility, making restitution, genuine repentance, requesting forgiveness.' }]
  },
  {
    id: 'mbti-couple-personality-match',
    name: 'MBTI Couple Personality Match',
    number: 52,
    phase: 4,
    phaseName: PHASE_NAMES[4],
    category: 'Personality',
    tagline: 'Myers-Briggs relationship dynamics and golden pair analysis',
    description: 'Select your and your partner’s 4-letter personality type to see your cognitive synergy.',
    detailedOverview: 'Unpacks how Introversion/Extraversion, Intuition/Sensing, Thinking/Feeling, and Judging/Perceiving interact.',
    howItWorks: ['Select Type A and Type B', 'Generate cognitive synergy analysis'],
    whatResultMeans: 'Explains why you see the world differently and how to turn differences into teamwork.',
    icon: 'Layers',
    isFunctional: true,
    tags: ['mbti love', 'personality match', 'infj enfp', 'intj enfp'],
    faqs: [{ question: 'What is a "Golden Pair"?', answer: 'Pairs with shared intuitive communication but balancing thinking/feeling functions (e.g., INFJ & ENFP).' }]
  },
  {
    id: 'enneagram-relationship-matrix',
    name: 'Enneagram Relationship Matrix',
    number: 53,
    phase: 4,
    phaseName: PHASE_NAMES[4],
    category: 'Personality',
    tagline: 'Types 1 through 9: discover your core fears, desires, and couple synergy',
    description: 'Compare any two Enneagram types to discover growth points and stress triggers.',
    detailedOverview: 'Deep dive into how your core personality motivations interact under security and stress.',
    howItWorks: ['Select your Enneagram type (1-9)', 'Select partner type', 'Receive matrix synergy report'],
    whatResultMeans: 'Shows how to support your partner’s deepest emotional needs.',
    icon: 'Hexagon',
    isFunctional: true,
    tags: ['enneagram', 'enneagram match', 'type 9 type 2'],
    faqs: [{ question: 'Can any two Enneagram types succeed?', answer: 'Yes! Self-aware, healthy levels of any type create magical partnerships.' }]
  },
  {
    id: 'introvert-love-language-guide',
    name: 'Introvert Love Language Guide',
    number: 54,
    phase: 4,
    phaseName: PHASE_NAMES[4],
    category: 'Love Languages',
    tagline: 'Quiet affection, parallel play, and loving an introvert deeply',
    description: 'Learn the unique ways introverts demonstrate and long to receive romantic care.',
    detailedOverview: 'Explores "parallel play" (reading together in silence), thoughtful written letters, and low-stimulus dates.',
    howItWorks: ['Check introvert affection traits', 'Get the Introvert Care Package guide'],
    whatResultMeans: 'Validates quiet, non-verbal expressions of deep, unwavering devotion.',
    icon: 'Coffee',
    isFunctional: true,
    tags: ['introvert love', 'parallel play', 'quiet romance'],
    faqs: [{ question: 'What is parallel play in adults?', answer: 'Sharing the same physical space doing independent relaxing activities without pressure to talk.' }]
  },
  {
    id: 'extrovert-energy-love-guide',
    name: 'Extrovert Energy Love Guide',
    number: 55,
    phase: 4,
    phaseName: PHASE_NAMES[4],
    category: 'Love Languages',
    tagline: 'Celebrations, shared adventures, and loving an extrovert loudly',
    description: 'Understand how extroverts recharge through enthusiastic sharing, dates with friends, and public pride.',
    detailedOverview: 'Helps partners understand that an extrovert’s social nature doesn’t detract from their unique romantic devotion.',
    howItWorks: ['Explore extrovert connection styles', 'Get actionable connection rituals'],
    whatResultMeans: 'Celebrates warmth, verbal enthusiasm, and celebratory romance.',
    icon: 'Sun',
    isFunctional: true,
    tags: ['extrovert love', 'social energy', 'vocal affection'],
    faqs: [{ question: 'How do extroverts feel most loved?', answer: 'When their partner enthusiastically listens to their ideas and joins their celebrations.' }]
  },
  {
    id: 'love-language-giving-vs-receiving',
    name: 'Giving vs Receiving Love Language Match',
    number: 56,
    phase: 4,
    phaseName: PHASE_NAMES[4],
    category: 'Love Languages',
    tagline: 'Do you give through gifts but want to receive words of affirmation?',
    description: 'Many people have different languages for expressing love vs feeling loved. Discover yours.',
    detailedOverview: 'Distinguishes between your outward expression dialect and your inward receptive craving.',
    howItWorks: ['Answer two-track questions for giving vs receiving', 'See your split love profile'],
    whatResultMeans: 'Prevents accidentally assuming your partner wants what you naturally give.',
    icon: 'ArrowLeftRight',
    isFunctional: true,
    tags: ['giving love', 'receiving love', 'split languages'],
    faqs: [{ question: 'Is it common to have split languages?', answer: 'Extremely common! Over 60% of people have distinct giving and receiving dialects.' }]
  },
  {
    id: 'words-of-affirmation-generator',
    name: 'Words of Affirmation Builder',
    number: 57,
    phase: 4,
    phaseName: PHASE_NAMES[4],
    category: 'Love Languages',
    tagline: 'Generate heartfelt, personalized compliments and love notes',
    description: 'Need words to express your gratitude? Build touching messages tailored to your partner.',
    detailedOverview: 'Helps people who struggle with verbal expression craft authentic, poetic affirmations.',
    howItWorks: ['Select partner qualities you admire', 'Generate customizable love notes & texts'],
    whatResultMeans: 'Gives you the exact right words to make your partner’s day unforgettable.',
    icon: 'FileText',
    isFunctional: true,
    badge: 'Popular',
    tags: ['affirmation builder', 'love letters', 'sweet texts'],
    faqs: [{ question: 'Can I copy these to send as texts?', answer: 'Yes! Instant 1-click copy for WhatsApp, iMessage, and sticky notes.' }]
  },
  {
    id: 'quality-time-date-planner',
    name: 'Quality Time Date Planner',
    number: 58,
    phase: 4,
    phaseName: PHASE_NAMES[4],
    category: 'Love Languages',
    tagline: 'Curate distraction-free dates for Quality Time lovers',
    description: 'Filter dates by budget, weather, and intimacy level to plan undivided connection time.',
    detailedOverview: 'Quality Time isn’t sitting next to each other on phones. It’s shared eye contact and deep presence.',
    howItWorks: ['Select vibe (cozy, adventurous, food, intellectual)', 'Get customized itinerary'],
    whatResultMeans: 'Transforms ordinary evenings into memorable bonding rituals.',
    icon: 'Hourglass',
    isFunctional: true,
    tags: ['quality time', 'date planner', 'undivided attention'],
    faqs: [{ question: 'What counts as true quality time?', answer: 'Activities where phones are put away and you engage in mutual conversation or shared flow.' }]
  },
  {
    id: 'acts-of-service-idea-vault',
    name: 'Acts of Service Idea Vault',
    number: 59,
    phase: 4,
    phaseName: PHASE_NAMES[4],
    category: 'Love Languages',
    tagline: 'Meaningful, practical ways to ease your partner’s burden',
    description: 'Discover 50+ thoughtful actions that say "I see your stress and I am here to help."',
    detailedOverview: 'For Acts of Service lovers, actions speak much louder than words. Find creative daily ideas.',
    howItWorks: ['Pick category (morning, workday, car, chores, rest)', 'Generate tailored action checklist'],
    whatResultMeans: 'Demonstrates reliable, proactive partnership in everyday life.',
    icon: 'Wrench',
    isFunctional: true,
    tags: ['acts of service', 'practical love', 'chores and care'],
    faqs: [{ question: 'What is the key to acts of service?', answer: 'Doing tasks without being asked, showing proactive mindfulness.' }]
  },
  {
    id: 'thoughtful-gifting-compass',
    name: 'Thoughtful Gifting Compass',
    number: 60,
    phase: 4,
    phaseName: PHASE_NAMES[4],
    category: 'Love Languages',
    tagline: 'Find sentimental, meaningful gifts that touch the heart',
    description: 'Move beyond generic gifts to find symbols of love that carry lasting emotional weight.',
    detailedOverview: 'For Gift lovers, the price tag is irrelevant; it’s the thought, memory, and timing that matter.',
    howItWorks: ['Answer questions about memories and partner passions', 'Receive tailored sentimental gift ideas'],
    whatResultMeans: 'Helps you give gifts that become treasured relationship keepsakes.',
    icon: 'Package',
    isFunctional: true,
    tags: ['gift ideas', 'thoughtful gifts', 'anniversary presents'],
    faqs: [{ question: 'Do gifts have to be expensive?', answer: 'Never! A pressed flower from your first hike together is worth more than expensive jewelry.' }]
  },
  {
    id: 'physical-touch-comfort-guide',
    name: 'Physical Touch & Comfort Guide',
    number: 61,
    phase: 4,
    phaseName: PHASE_NAMES[4],
    category: 'Love Languages',
    tagline: 'Gentle touch rituals, hugs, and nervous system co-regulation',
    description: 'Explore the science of oxytocin and non-sexual affectionate touch for couples.',
    detailedOverview: 'A 20-second hug lowers cortisol and stimulates oxytocin. Learn daily touch rituals.',
    howItWorks: ['Review touch comfort preferences', 'Receive co-regulation touch exercises'],
    whatResultMeans: 'Brings deep physiological grounding and reassurance into your home.',
    icon: 'Sparkles',
    isFunctional: true,
    tags: ['physical touch', 'oxytocin', 'cuddling rituals'],
    faqs: [{ question: 'Why does a 20-second hug work?', answer: 'It takes approximately 20 continuous seconds of embrace for the brain to release oxytocin.' }]
  },
  {
    id: 'love-archetype-test',
    name: 'Love Archetype Test',
    number: 62,
    phase: 4,
    phaseName: PHASE_NAMES[4],
    category: 'Personality',
    tagline: 'Are you The Anchor, The Dreamer, The Protector, or The Muse?',
    description: 'Discover your timeless romantic archetype and what kind of partner draws out your best self.',
    detailedOverview: 'Based on Jungian relationship archetypes, revealing your instinctual romantic posture.',
    howItWorks: ['Answer 6 narrative questions', 'Reveal your Archetype Badge & Partner Match'],
    whatResultMeans: 'Illuminates your romantic strengths and shadow tendencies to watch out for.',
    icon: 'Sparkle',
    isFunctional: true,
    tags: ['archetypes', 'romantic identity', 'jungian love'],
    faqs: [{ question: 'Can archetypes evolve?', answer: 'Yes, as you heal and grow, your archetype matures into its highest expression.' }]
  },
  {
    id: 'empathy-quotient-for-couples',
    name: 'Empathy Quotient for Couples',
    number: 63,
    phase: 4,
    phaseName: PHASE_NAMES[4],
    category: 'Personality',
    tagline: 'Test your emotional attunement and perspective-taking skill',
    description: 'How accurately can you step into your partner’s shoes and understand their world?',
    detailedOverview: 'Measures cognitive empathy (understanding thoughts) and affective empathy (feeling emotions).',
    howItWorks: ['Answer perspective scenarios', 'Score your Couple Empathy Index'],
    whatResultMeans: 'Provides listening tools to validate your partner before offering solutions.',
    icon: 'HeartHandshake',
    isFunctional: true,
    tags: ['empathy', 'active listening', 'understanding partner'],
    faqs: [{ question: 'What is cognitive empathy?', answer: 'The ability to logically understand why someone feels the way they do, even if you disagree.' }]
  },
  {
    id: 'romantic-spontaneity-index',
    name: 'Romantic Spontaneity Index',
    number: 64,
    phase: 4,
    phaseName: PHASE_NAMES[4],
    category: 'Personality',
    tagline: 'Are you a spontaneous romantic or a thoughtful planner?',
    description: 'Discover your surprise style and how to plan spontaneous moments with ease.',
    detailedOverview: 'Measures how naturally you execute surprise dates, random love notes, and impromptu dances in the kitchen.',
    howItWorks: ['Score your impulse romantic habits', 'Get effortless spontaneity prompts'],
    whatResultMeans: 'Keeps romance feeling alive without creating stress.',
    icon: 'Zap',
    isFunctional: true,
    tags: ['spontaneity', 'surprise dates', 'romantic impulse'],
    faqs: [{ question: 'How can a planner be spontaneous?', answer: 'Schedule "spontaneous blocks" on the calendar with no predetermined activity.' }]
  },
  {
    id: 'assertiveness-vs-accommodation-balance',
    name: 'Assertiveness vs Accommodation Balance',
    number: 65,
    phase: 4,
    phaseName: PHASE_NAMES[4],
    category: 'Personality',
    tagline: 'Do you speak your needs clearly or stay silent to keep the peace?',
    description: 'Ensure you maintain your voice and boundaries while caring for your partner.',
    detailedOverview: 'People-pleasing leads to quiet resentment. Find healthy, loving assertiveness.',
    howItWorks: ['Answer communication boundary questions', 'Calculate your voice-balance rating'],
    whatResultMeans: 'Teaches non-defensive ways to ask for what you need with warmth.',
    icon: 'MessageSquare',
    isFunctional: true,
    tags: ['boundaries', 'people pleasing', 'speaking up'],
    faqs: [{ question: 'Is accommodating healthy?', answer: 'Flexibility is healthy; ignoring your own core needs to avoid temporary discomfort is not.' }]
  },
  {
    id: 'patience-and-temperament-test',
    name: 'Patience & Temperament Test',
    number: 66,
    phase: 4,
    phaseName: PHASE_NAMES[4],
    category: 'Personality',
    tagline: 'How well do you stay calm during delays, traffic, and mishaps?',
    description: 'Explore your daily frustration tolerance and how it impacts your relationship atmosphere.',
    detailedOverview: 'A partner’s patience in trivial moments sets the safety tone for major challenges.',
    howItWorks: ['Rate reactions to daily stressors', 'Receive patience enhancement tips'],
    whatResultMeans: 'Gives breathing cues to stay relaxed when plans go sideways.',
    icon: 'Shield',
    isFunctional: true,
    tags: ['patience', 'calm', 'emotional regulation'],
    faqs: [{ question: 'How to practice patience?', answer: 'Pause for 3 slow belly breaths before reacting when plans change.' }]
  },
  {
    id: 'optimism-vs-realism-couple-match',
    name: 'Optimism vs Realism Couple Match',
    number: 67,
    phase: 4,
    phaseName: PHASE_NAMES[4],
    category: 'Personality',
    tagline: 'The Dreamer and The Pragmatist: turning perspective differences into power',
    description: 'See how an optimistic visionary and a realistic anchor build balanced dreams together.',
    detailedOverview: 'The optimist brings hope and vision; the realist builds the scaffolding and safety net.',
    howItWorks: ['Assess outlook on risk and possibilities', 'Score your perspective balance'],
    whatResultMeans: 'Teaches both partners to value the other’s lens as protection and fuel.',
    icon: 'Glasses',
    isFunctional: true,
    tags: ['optimist', 'realist', 'dreamer'],
    faqs: [{ question: 'Why do optimists and realists often marry?', answer: 'They prevent each other from crashing into recklessness or despair.' }]
  },

  // ==========================================
  // PHASE 5: COUPLE GAMES & QUESTIONS (68 - 83)
  // ==========================================
  {
    id: 'would-you-rather-couples-edition',
    name: 'Would You Rather? — Couples Edition',
    number: 68,
    phase: 5,
    phaseName: PHASE_NAMES[5],
    category: 'Couple Games',
    tagline: '100+ hilarious, romantic, and impossible dilemmas for couples',
    description: 'Would you rather always hold hands in public or have a secret telepathic language?',
    detailedOverview: 'A delightful conversation starter for road trips, dinner dates, and lazy Sunday mornings.',
    howItWorks: ['Pick category', 'Swipe through dilemma cards', 'Discuss your hilarious choices'],
    whatResultMeans: 'Unlocks unexpected stories and memories from your partner’s life.',
    icon: 'HelpCircle',
    isFunctional: true,
    badge: 'Popular',
    tags: ['would you rather', 'couples game', 'road trip game'],
    faqs: [{ question: 'How many dilemmas are included?', answer: 'Over 100 categorized questions with continuous shuffling.' }]
  },
  {
    id: 'never-have-i-ever-couples-edition',
    name: 'Never Have I Ever — Couples Edition',
    number: 69,
    phase: 5,
    phaseName: PHASE_NAMES[5],
    category: 'Couple Games',
    tagline: 'Spill secrets, funny memories, and sweet confessions',
    description: 'The classic party game reimagined with sweet, funny, and adventurous couple prompts.',
    detailedOverview: 'Put 10 fingers up and see who has done what. Perfect for double dates or private giggles.',
    howItWorks: ['Tap to reveal a prompt', 'Admit if you have done it', 'Share the backstory!'],
    whatResultMeans: 'Deepens vulnerability through laughter and storytelling.',
    icon: 'Sparkles',
    isFunctional: true,
    tags: ['never have i ever', 'couples game', 'confessions'],
    faqs: [{ question: 'Are there spicy and clean modes?', answer: 'Yes, filter between Sweet, Funny, Travel, and Daring.' }]
  },
  {
    id: 'truth-or-dare-for-couples',
    name: 'Truth or Dare — Couples Edition',
    number: 70,
    phase: 5,
    phaseName: PHASE_NAMES[5],
    category: 'Couple Games',
    tagline: 'Deep romantic truths and playful romantic dares',
    description: 'Test your courage with touching confessions and fun affectionate challenges.',
    detailedOverview: 'Tailored specifically for couples to bring romantic excitement without awkwardness.',
    howItWorks: ['Choose Truth or Dare', 'Complete the prompt together', 'Rate each other’s execution!'],
    whatResultMeans: 'Breaks everyday seriousness with lighthearted play.',
    icon: 'Flame',
    isFunctional: true,
    badge: 'Popular',
    tags: ['truth or dare', 'date night game', 'romantic dare'],
    faqs: [{ question: 'What kind of dares are included?', answer: 'Playful romance: slow dancing without music, giving a foot rub, or speaking in accents.' }]
  },
  {
    id: '36-questions-to-fall-in-love',
    name: '36 Questions to Fall in Love',
    number: 71,
    phase: 5,
    phaseName: PHASE_NAMES[5],
    category: 'Couple Games',
    tagline: 'Dr. Arthur Aron’s famous psychology study questions',
    description: 'The proven scientific sequence of vulnerability questions designed to accelerate intimacy.',
    detailedOverview: 'Divided into three progressive sets moving from lighthearted to profound soul vulnerability.',
    howItWorks: ['Progress through Set 1, 2, and 3', 'Take turns answering each prompt truthfully'],
    whatResultMeans: 'Creates deep mutual intimacy by systematically lowering defenses together.',
    icon: 'BookOpen',
    isFunctional: true,
    badge: 'Top Pick',
    tags: ['36 questions', 'arthur aron', 'psychology of love'],
    faqs: [{ question: 'Why does this work so effectively?', answer: 'Gradual, reciprocal, and escalating self-disclosure triggers deep attachment.' }]
  },
  {
    id: 'date-night-roulette-spinner',
    name: 'Date Night Roulette Spinner',
    number: 72,
    phase: 5,
    phaseName: PHASE_NAMES[5],
    category: 'Couple Games',
    tagline: 'Spin the wheel to decide food, activity, and movie instantly',
    description: 'End the "I don’t care, what do you want?" dinner paralysis once and for all.',
    detailedOverview: 'An interactive 3-wheel spinner that locks in your cuisine, fun activity, and film genre.',
    howItWorks: ['Click Spin', 'Watch the wheels turn', 'Commit to the fun outcome!'],
    whatResultMeans: 'Saves 45 minutes of indecision and turns planning into a game.',
    icon: 'Disc',
    isFunctional: true,
    badge: 'Trending',
    tags: ['date night wheel', 'what to eat', 'indecision solver'],
    faqs: [{ question: 'Can we re-spin if we don’t like it?', answer: 'You get one veto pass per evening!' }]
  },
  {
    id: 'first-date-conversation-sparker',
    name: 'First Date Conversation Sparker',
    number: 73,
    phase: 5,
    phaseName: PHASE_NAMES[5],
    category: 'Couple Games',
    tagline: 'Skip the boring small talk with engaging, natural icebreakers',
    description: 'Thoughtful, creative questions to spark real chemistry and genuine storytelling on a first date.',
    detailedOverview: 'Never suffer through awkward silences again. Categorized into travel, passions, and dreams.',
    howItWorks: ['Draw prompt cards', 'Keep the conversational energy flowing naturally'],
    whatResultMeans: 'Helps both people feel comfortable and reveal their authentic personalities.',
    icon: 'MessagesSquare',
    isFunctional: true,
    tags: ['first date', 'icebreakers', 'conversation starters'],
    faqs: [{ question: 'Are these questions intrusive?', answer: 'No, they are warm, open-ended, and easy to answer at your own comfort level.' }]
  },
  {
    id: 'deep-questions-for-late-nights',
    name: 'Deep Questions for Late Nights',
    number: 74,
    phase: 5,
    phaseName: PHASE_NAMES[5],
    category: 'Couple Games',
    tagline: '2 AM soul talks: dreams, fears, mortality, and the universe',
    description: 'When the world is quiet, turn off the lights and dive into deep philosophical inquiries.',
    detailedOverview: 'Designed for pillow talk and late-night stargazing sessions with your favorite person.',
    howItWorks: ['Shuffle late-night cards', 'Take time reflecting and answering with honesty'],
    whatResultMeans: 'Strengthens your mental and spiritual bond at the deepest level.',
    icon: 'Moon',
    isFunctional: true,
    tags: ['late night talk', 'deep questions', 'pillow talk'],
    faqs: [{ question: 'When is best to use this tool?', answer: 'During quiet evenings with dim lighting and warm tea.' }]
  },
  {
    id: 'anniversary-reminiscence-game',
    name: 'Anniversary Reminiscence Game',
    number: 75,
    phase: 5,
    phaseName: PHASE_NAMES[5],
    category: 'Couple Games',
    tagline: 'Celebrate your journey by revisiting your sweetest memories',
    description: 'Do you remember the outfit they wore on date three? Relive the milestones of your love story.',
    detailedOverview: 'Re-walking your shared history reminds couples why they fell in love in the first place.',
    howItWorks: ['Prompt cards about firsts, trips, overcoming hurdles, and big laughs', 'Relive the memories'],
    whatResultMeans: 'Infuses your relationship with nostalgia, pride, and renewed commitment.',
    icon: 'History',
    isFunctional: true,
    tags: ['anniversary game', 'memories', 'relationship history'],
    faqs: [{ question: 'Can this be played at an anniversary dinner?', answer: 'Yes! It’s the perfect dinner table activity.' }]
  },
  {
    id: 'bucket-list-for-couples',
    name: 'Couple Bucket List Builder',
    number: 76,
    phase: 5,
    phaseName: PHASE_NAMES[5],
    category: 'Couple Games',
    tagline: 'Co-create your dream adventures, travel goals, and life dreams',
    description: 'Build, save, and check off 50+ unforgettable couple goals and shared adventures.',
    detailedOverview: 'Dreaming together creates future pull that energizes daily life and travel planning.',
    howItWorks: ['Browse categories (Travel, Thrills, Romance, Home)', 'Add to your shared bucket list'],
    whatResultMeans: 'A tangible roadmap for future memories you will look back on when you are old.',
    icon: 'CheckSquare',
    isFunctional: true,
    badge: 'Popular',
    tags: ['bucket list', 'couple goals', 'adventures'],
    faqs: [{ question: 'Can we save our bucket list?', answer: 'Yes, copy or export your personalized couple checklist anytime.' }]
  },
  {
    id: 'road-trip-couples-trivia',
    name: 'Road Trip Couples Trivia',
    number: 77,
    phase: 5,
    phaseName: PHASE_NAMES[5],
    category: 'Couple Games',
    tagline: 'Pass highway miles with music, pop culture, and relationship trivia',
    description: 'The ultimate passenger and driver game to turn long drives into laugh-filled memories.',
    detailedOverview: 'Keeps both driver and copilot entertained with quick-fire trivia and banter.',
    howItWorks: ['Select rounds', 'Score points against each other or as a team against the clock'],
    whatResultMeans: 'Makes long road trips fly by in a flash.',
    icon: 'Car',
    isFunctional: true,
    tags: ['road trip', 'trivia', 'car games'],
    faqs: [{ question: 'Is it hands-free friendly?', answer: 'Big bold buttons and readable text for easy co-pilot navigation.' }]
  },
  {
    id: 'dinner-table-question-cards',
    name: 'Dinner Table Question Cards',
    number: 78,
    phase: 5,
    phaseName: PHASE_NAMES[5],
    category: 'Couple Games',
    tagline: 'Replace "How was your day?" with fascinating evening prompts',
    description: 'Fresh dinner questions to help couples connect after a long workday.',
    detailedOverview: '"How was work?" usually gets a one-word answer. These prompts spark vibrant storytelling.',
    howItWorks: ['Draw a dinner card each evening', 'Enjoy meaningful dinner discussions'],
    whatResultMeans: 'Transforms dinner from a routine meal into an intentional connection ritual.',
    icon: 'Utensils',
    isFunctional: true,
    tags: ['dinner cards', 'evening talks', 'after work check in'],
    faqs: [{ question: 'How many dinner cards are included?', answer: 'Dozens of fresh cards updated regularly.' }]
  },
  {
    id: 'two-truths-and-a-lie-couples',
    name: 'Two Truths & A Lie — Couples Edition',
    number: 79,
    phase: 5,
    phaseName: PHASE_NAMES[5],
    category: 'Couple Games',
    tagline: 'Can you fool your partner with obscure facts from your past?',
    description: 'Create two truths and one lie from your childhood, travels, or hidden talents.',
    detailedOverview: 'Even after 10 years, partners still have surprising secrets from their past.',
    howItWorks: ['Input statements', 'Partner guesses which is the sneaky lie'],
    whatResultMeans: 'Discovers fascinating childhood stories you never knew before.',
    icon: 'Sparkles',
    isFunctional: true,
    tags: ['two truths one lie', 'couple game', 'childhood secrets'],
    faqs: [{ question: 'What makes a great lie in this game?', answer: 'A believable mundane detail mixed with two outrageous real truths!' }]
  },
  {
    id: 'rapid-fire-this-or-that-couples',
    name: 'Rapid-Fire This or That? — Couples',
    number: 80,
    phase: 5,
    phaseName: PHASE_NAMES[5],
    category: 'Couple Games',
    tagline: 'Beach or Mountains? Coffee or Tea? 50 lightning-fast choices',
    description: 'Answer simultaneously in 3 seconds to test your instinctual alignment.',
    detailedOverview: 'High energy, fast-paced party game that keeps couples laughing and teasing each other.',
    howItWorks: ['Countdown: 3, 2, 1... speak your choice at the same time!', 'Tally matches'],
    whatResultMeans: 'Shows how in-sync your everyday preferences are.',
    icon: 'Flame',
    isFunctional: true,
    tags: ['this or that', 'rapid fire', 'speed game'],
    faqs: [{ question: 'How fast should we answer?', answer: 'Instant gut reaction—no overthinking allowed!' }]
  },
  {
    id: 'compliment-battle-game',
    name: 'Compliment Battle Game',
    number: 81,
    phase: 5,
    phaseName: PHASE_NAMES[5],
    category: 'Couple Games',
    tagline: 'Take turns dropping sincere, creative compliments until someone blushes',
    description: 'A loving duel of words where the only rule is you cannot repeat a compliment.',
    detailedOverview: 'Builds positive sentiment override and leaves both partners glowing with confidence.',
    howItWorks: ['Take turns delivering compliments', 'Tap when you make your partner smile or blush!'],
    whatResultMeans: 'A heartwarming exercise that floods your brain with dopamine and warmth.',
    icon: 'Award',
    isFunctional: true,
    tags: ['compliment game', 'blush challenge', 'romantic words'],
    faqs: [{ question: 'Who wins the battle?', answer: 'Whoever brings the biggest genuine smile wins the round!' }]
  },
  {
    id: 'fantasy-travel-itinerary-builder',
    name: 'Fantasy Travel Itinerary Builder',
    number: 82,
    phase: 5,
    phaseName: PHASE_NAMES[5],
    category: 'Couple Games',
    tagline: 'If money was no object, design your dream 7-day romantic escape',
    description: 'Pick exotic locations, luxury villas, adventure activities, and sunset dinners together.',
    detailedOverview: 'Planning fantasy trips together activates excitement and clarifies dream vacations.',
    howItWorks: ['Choose destination, lodging, and activities', 'Export your dream itinerary card'],
    whatResultMeans: 'Inspires your next real-world vacation savings goal.',
    icon: 'Palmtree',
    isFunctional: true,
    tags: ['travel planner', 'dream vacation', 'fantasy trip'],
    faqs: [{ question: 'Can we save the generated itinerary?', answer: 'Yes, copy or share your dream itinerary with a single tap.' }]
  },
  {
    id: 'kiss-and-cuddle-dare-cards',
    name: 'Kiss & Cuddle Dare Cards',
    number: 83,
    phase: 5,
    phaseName: PHASE_NAMES[5],
    category: 'Couple Games',
    tagline: 'Sweet, gentle, and affectionate micro-challenges for cozy nights',
    description: 'Draw sweet cards like "30-second forehead kiss", "trace their hand", or "slow sway".',
    detailedOverview: 'Brings gentle romance and sweetness into your living room after a long week.',
    howItWorks: ['Draw affection card', 'Fulfill the sweet prompt together'],
    whatResultMeans: 'Fosters safe, gentle physical closeness and tenderness.',
    icon: 'Heart',
    isFunctional: true,
    tags: ['cuddle cards', 'sweet dares', 'gentle romance'],
    faqs: [{ question: 'Is this suitable for all couples?', answer: 'Yes, all prompts focus on heartwarming affection and gentle romance.' }]
  },

  // ==========================================
  // PHASE 6: RELATIONSHIP CALCULATORS (84 - 100)
  // ==========================================
  {
    id: 'next-anniversary-countdown',
    name: 'Next Anniversary Countdown',
    number: 84,
    phase: 6,
    phaseName: PHASE_NAMES[6],
    category: 'Calculators',
    tagline: 'Live ticking countdown to your next wedding or dating anniversary',
    description: 'Never forget an anniversary again. Displays exact days, hours, minutes, and seconds remaining.',
    detailedOverview: 'Keep anticipation high for your next big celebration with an exact real-time ticker.',
    howItWorks: ['Enter your anniversary month and day', 'Get live ticking countdown and gift ideas'],
    whatResultMeans: 'Gives you plenty of advance notice to plan gifts, reservations, and surprises.',
    icon: 'Timer',
    isFunctional: true,
    badge: 'Popular',
    tags: ['anniversary countdown', 'next anniversary', 'reminder'],
    faqs: [{ question: 'Does this handle leap years?', answer: 'Yes, mathematically adjusted for leap years automatically.' }]
  },
  {
    id: 'traditional-anniversary-gift-finder',
    name: 'Traditional & Modern Anniversary Gift Finder',
    number: 85,
    phase: 6,
    phaseName: PHASE_NAMES[6],
    category: 'Calculators',
    tagline: 'Year 1 Paper, Year 5 Wood, Year 25 Silver, Year 50 Gold & modern equivalents',
    description: 'Find the official traditional, modern, gemstone, and flower themes for every year of love.',
    detailedOverview: 'A comprehensive directory of traditional gift themes from Year 1 all the way to Year 60.',
    howItWorks: ['Input your anniversary year number', 'Reveal traditional, modern, floral, and gem symbols'],
    whatResultMeans: 'Offers creative, meaningful inspiration so you never give boring gifts.',
    icon: 'Gift',
    isFunctional: true,
    tags: ['anniversary gifts', 'traditional gifts', 'silver anniversary', 'paper anniversary'],
    faqs: [{ question: 'What is the 1st anniversary gift?', answer: 'Traditional is Paper (representing a blank slate); Modern is Clocks (representing time together).' }]
  },
  {
    id: 'hours-spent-together-calculator',
    name: 'Hours Spent Together Calculator',
    number: 86,
    phase: 6,
    phaseName: PHASE_NAMES[6],
    category: 'Calculators',
    tagline: 'Estimate total awake, sleep, and quality conversation hours shared',
    description: 'Calculate the millions of minutes and thousands of hours you’ve invested in your bond.',
    detailedOverview: 'Breaks down your relationship tenure into estimated awake hours, sleep hours, and date nights.',
    howItWorks: ['Enter start date', 'Select living status', 'Calculate grand total shared hours'],
    whatResultMeans: 'Puts the depth of your shared life into inspiring perspective.',
    icon: 'Hourglass',
    isFunctional: true,
    tags: ['hours together', 'time spent', 'minutes calculator'],
    faqs: [{ question: 'How is sleep time calculated?', answer: 'Based on an average of 7-8 shared resting hours per night.' }]
  },
  {
    id: 'couples-age-gap-calculator',
    name: 'Couples Age Gap Calculator',
    number: 87,
    phase: 6,
    phaseName: PHASE_NAMES[6],
    category: 'Calculators',
    tagline: 'Exact age difference in years, days, and astrological generational alignment',
    description: 'Calculate the exact age difference between two partners down to the day.',
    detailedOverview: 'Shows your generational cohorts (e.g. Gen Z / Millennial) and provides healthy age-gap tips.',
    howItWorks: ['Enter both birthdates', 'Compute exact day, month, and year gap'],
    whatResultMeans: 'Celebrates how different life perspectives enrich your partnership.',
    icon: 'Calendar',
    isFunctional: true,
    tags: ['age gap', 'birthday comparison', 'generational difference'],
    faqs: [{ question: 'Does age gap affect relationship longevity?', answer: 'Emotional maturity and shared values matter far more than biological age differences.' }]
  },
  {
    id: 'wedding-budget-estimator',
    name: 'Wedding Budget Estimator',
    number: 88,
    phase: 6,
    phaseName: PHASE_NAMES[6],
    category: 'Calculators',
    tagline: 'Allocate venue, catering, photo, attire, and floral costs smoothly',
    description: 'Input your total budget to receive industry standard percentage breakdowns across categories.',
    detailedOverview: 'Prevents budget shock by allocating funds across venue (40%), photo/video (12%), attire (8%), etc.',
    howItWorks: ['Enter target budget and guest count', 'Receive itemized category breakdown'],
    whatResultMeans: 'Keeps wedding planning realistic and stress-free.',
    icon: 'Coins',
    isFunctional: true,
    tags: ['wedding budget', 'wedding planner', 'estimator'],
    faqs: [{ question: 'What is the biggest wedding expense?', answer: 'Catering and venue typically comprise 40-50% of the total budget.' }]
  },
  {
    id: 'wedding-guest-count-calculator',
    name: 'Wedding Guest Count & Seating Estimator',
    number: 89,
    phase: 6,
    phaseName: PHASE_NAMES[6],
    category: 'Calculators',
    tagline: 'Calculate RSVP acceptance rates, table counts, and seating arrangements',
    description: 'Estimate real attendance based on local vs out-of-town guest ratios.',
    detailedOverview: 'Typically 15-20% of invited guests RSVP "no". Accurately plan seating and catering headcount.',
    howItWorks: ['Input invited guest count', 'Adjust destination and local ratios', 'Get estimated final headcount'],
    whatResultMeans: 'Saves thousands of dollars on excess catering commitments.',
    icon: 'Users',
    isFunctional: true,
    tags: ['guest list', 'wedding rsvp', 'headcount'],
    faqs: [{ question: 'What is typical RSVP drop-off?', answer: 'Local weddings see 80-85% attendance; destination weddings see 60-75% attendance.' }]
  },
  {
    id: 'honeymoon-budget-and-savings-planner',
    name: 'Honeymoon Savings & Budget Planner',
    number: 90,
    phase: 6,
    phaseName: PHASE_NAMES[6],
    category: 'Calculators',
    tagline: 'Calculate monthly savings needed to fund your dream romantic getaway',
    description: 'Input target trip cost and wedding date to calculate exact weekly or monthly savings needed.',
    detailedOverview: 'Turns daunting vacation goals into manageable, automated monthly savings milestones.',
    howItWorks: ['Enter dream honeymoon budget and months remaining', 'Get exact monthly savings plan'],
    whatResultMeans: 'Enjoy your honeymoon completely debt-free upon departure.',
    icon: 'Plane',
    isFunctional: true,
    tags: ['honeymoon savings', 'vacation budget', 'couples money'],
    faqs: [{ question: 'How to save faster?', answer: 'Consider a honeymoon registry where guests contribute to excursions instead of toaster ovens.' }]
  },
  {
    id: 'date-night-annual-spend-calculator',
    name: 'Date Night Annual Spend Calculator',
    number: 91,
    phase: 6,
    phaseName: PHASE_NAMES[6],
    category: 'Calculators',
    tagline: 'See how much you invest annually in romantic dinners and activities',
    description: 'Calculate weekly or bi-weekly date costs and find ways to optimize romance ROI.',
    detailedOverview: 'Investing in dates is investing in relationship insurance. Plan your yearly romance budget.',
    howItWorks: ['Input frequency and average date spend', 'See annual total and savings ideas'],
    whatResultMeans: 'Proves that consistency beats one expensive annual trip every time.',
    icon: 'Receipt',
    isFunctional: true,
    tags: ['date budget', 'annual spend', 'investing in love'],
    faqs: [{ question: 'What is a reasonable date budget?', answer: 'Many couples target 3-5% of monthly discretionary income for dates.' }]
  },
  {
    id: 'shared-chores-equity-calculator',
    name: 'Shared Chores & Invisible Labor Calculator',
    number: 92,
    phase: 6,
    phaseName: PHASE_NAMES[6],
    category: 'Calculators',
    tagline: 'Balance cooking, cleaning, bills, and mental load fairly',
    description: 'Measure the hours each partner contributes to household chores and cognitive load.',
    detailedOverview: 'Brings transparency to invisible household tasks to prevent domestic burnout.',
    howItWorks: ['Log weekly hours on cooking, laundry, errands, and planning', 'View equity balance pie chart'],
    whatResultMeans: 'Encourages fair adjustments without nagging or resentment.',
    icon: 'Scale',
    isFunctional: true,
    tags: ['chores equity', 'mental load', 'fair play'],
    faqs: [{ question: 'What is invisible labor?', answer: 'The cognitive effort of planning meals, restocking supplies, and managing schedules.' }]
  },
  {
    id: 'heartbeats-shared-together-counter',
    name: 'Heartbeats Shared Together Counter',
    number: 93,
    phase: 6,
    phaseName: PHASE_NAMES[6],
    category: 'Calculators',
    tagline: 'Calculate the millions of biological heartbeats you’ve lived through side-by-side',
    description: 'At ~100,000 heartbeats per day, calculate your staggering shared heartbeat total.',
    detailedOverview: 'A deeply poetic, scientific reflection on the biological rhythm of your journey together.',
    howItWorks: ['Enter start date', 'Watch live animated heartbeat ticker tally your shared beats'],
    whatResultMeans: 'A romantic metric to write in cards or celebrate on milestones.',
    icon: 'HeartPulse',
    isFunctional: true,
    tags: ['heartbeats', 'poetic calculator', 'time together'],
    faqs: [{ question: 'How many heartbeats per year?', answer: 'An average human heart beats around 36 to 40 million times per year.' }]
  },
  {
    id: 'ldr-flight-distance-timezones-calculator',
    name: 'LDR Distance & Timezones Calculator',
    number: 94,
    phase: 6,
    phaseName: PHASE_NAMES[6],
    category: 'Calculators',
    tagline: 'Calculate miles apart, timezone offset, and ideal overlap calling windows',
    description: 'Input city A and city B to see exact distance and synchronized morning/evening call times.',
    detailedOverview: 'A lifesaver for international and cross-country long-distance couples coordinating calls.',
    howItWorks: ['Select two locations', 'Get flight miles, timezone math, and best FaceTime windows'],
    whatResultMeans: 'Removes the guesswork from "Is it too late to call?"',
    icon: 'Compass',
    isFunctional: true,
    tags: ['ldr distance', 'timezone math', 'calling window'],
    faqs: [{ question: 'How to handle 8+ hour timezone gaps?', answer: 'Establish a morning voice note habit so they wake up to your voice.' }]
  },
  {
    id: 'shared-sleep-hours-counter',
    name: 'Shared Sleep & Dreaming Hours Counter',
    number: 95,
    phase: 6,
    phaseName: PHASE_NAMES[6],
    category: 'Calculators',
    tagline: 'Count the thousands of night hours spent resting side-by-side',
    description: 'Calculate the cumulative nights, REM cycles, and bedtime hours spent beside each other.',
    detailedOverview: 'Sleeping together fosters deep subconscious bonding and nervous system calm.',
    howItWorks: ['Input co-sleeping start date', 'Tally sleep hours and shared morning coffees'],
    whatResultMeans: 'Celebrates the comforting sanctuary of shared restful nights.',
    icon: 'Moon',
    isFunctional: true,
    tags: ['sleep together', 'dreaming hours', 'bedtime counter'],
    faqs: [{ question: 'What is the sleep benefit of couples?', answer: 'Studies show sleeping with a trusted partner improves REM duration and lowers blood pressure.' }]
  },
  {
    id: 'sunrises-and-sunsets-shared-counter',
    name: 'Sunrises & Sunsets Shared Counter',
    number: 96,
    phase: 6,
    phaseName: PHASE_NAMES[6],
    category: 'Calculators',
    tagline: 'Tally the thousands of dawns and twilight skies you’ve shared together',
    description: 'How many days has the sun risen on your love? Calculate your golden hour total.',
    detailedOverview: 'Each sunrise marks a new day chosen to walk through life side-by-side.',
    howItWorks: ['Enter your relationship start date', 'Calculate total sunrises, sunsets, and lunar cycles'],
    whatResultMeans: 'A poetic celebration perfect for anniversary toasts and photo albums.',
    icon: 'Sun',
    isFunctional: true,
    tags: ['sunrises', 'sunsets', 'poetic counter'],
    faqs: [{ question: 'Can this be exported as a graphic?', answer: 'Yes! Instant 1-click shareable badge.' }]
  },
  {
    id: 'shared-meals-eaten-calculator',
    name: 'Shared Meals & Dinners Calculator',
    number: 97,
    phase: 6,
    phaseName: PHASE_NAMES[6],
    category: 'Calculators',
    tagline: 'Estimate breakfasts, lunches, and romantic dinners shared',
    description: 'Count the thousands of times you’ve broken bread and shared table conversations.',
    detailedOverview: 'Sharing food is one of humanity’s oldest intimacy rituals. Tally your feast total.',
    howItWorks: ['Input years together and estimated weekly meals', 'Get grand meal count and culinary memories'],
    whatResultMeans: 'Reminds couples of the rich daily sustenance found at the kitchen table.',
    icon: 'UtensilsCrossed',
    isFunctional: true,
    tags: ['meals together', 'dinners eaten', 'food memories'],
    faqs: [{ question: 'Why are shared meals important?', answer: 'Regular shared meals lower relationship anxiety and increase conversational openness.' }]
  },
  {
    id: 'milestone-1000-days-calculator',
    name: '1,000 Days of Love Calculator',
    number: 98,
    phase: 6,
    phaseName: PHASE_NAMES[6],
    category: 'Calculators',
    tagline: 'When is your 1,000th day? Calculate and plan a magical celebration',
    description: 'The 1,000-day milestone (~2.74 years) is a major worldwide couple tradition. Find your exact date.',
    detailedOverview: 'An extraordinary milestone that falls between year 2 and year 3. Discover your milestone date.',
    howItWorks: ['Enter your relationship start date', 'Calculate exact 1,000th day calendar date and countdown'],
    whatResultMeans: 'Gives couples a unique, unexpected anniversary to celebrate with friends or a trip.',
    icon: 'Trophy',
    isFunctional: true,
    badge: 'Popular',
    tags: ['1000 days', 'thousand days of love', 'milestone date'],
    faqs: [{ question: 'How many years is 1,000 days?', answer: 'Approximately 2 years, 8 months, and 26 days.' }]
  },
  {
    id: 'golden-anniversary-timeline-projector',
    name: '50-Year Golden Timeline Projector',
    number: 99,
    phase: 6,
    phaseName: PHASE_NAMES[6],
    category: 'Calculators',
    tagline: 'Project your future milestones: Silver (25 yr), Pearl (30 yr), and Gold (50 yr)',
    description: 'See the exact future calendar years and dates when you will reach major historic love milestones.',
    detailedOverview: 'A lifetime view of love showing the years when you’ll celebrate Ruby, Sapphire, and Gold.',
    howItWorks: ['Input start or wedding date', 'Receive comprehensive 50-year milestone calendar'],
    whatResultMeans: 'Inspires lifelong commitment and deep long-term perspective.',
    icon: 'Sparkles',
    isFunctional: true,
    tags: ['50 year anniversary', 'golden jubilee', 'silver anniversary'],
    faqs: [{ question: 'What are the biggest milestones?', answer: 'Year 1 (Paper), Year 10 (Tin/Aluminum), Year 25 (Silver), Year 50 (Gold).' }]
  },
  {
    id: 'relationship-roi-and-growth-score',
    name: 'Relationship Growth & Gratitude Score',
    number: 100,
    phase: 6,
    phaseName: PHASE_NAMES[6],
    category: 'Calculators',
    tagline: 'The ultimate 100th tool: celebrate emotional growth, resilience, and happiness',
    description: 'A culminating tool honoring how much you have grown, learned, and flourished together.',
    detailedOverview: 'Tool #100 celebrates the true purpose of LoveScoreTest: recognizing that genuine love is a living, evolving garden nurtured by mutual respect, laughter, and daily choice.',
    howItWorks: ['Reflect on 5 areas of personal and couple growth', 'Generate your Grand Relationship Growth Badge'],
    whatResultMeans: 'A joyful crowning achievement badge honoring your shared resilience and love.',
    icon: 'Crown',
    isFunctional: true,
    badge: 'Top Pick',
    tags: ['relationship growth', 'love journey', 'tool 100', 'celebration'],
    faqs: [{ question: 'What is the greatest relationship investment?', answer: 'Consistently choosing empathy and curiosity over defensiveness every single day.' }]
  }
];

export const TOOL_CATEGORIES = CATEGORIES;

export const TOOL_PHASES = [
  { phase: 1, name: 'Love Calculators' },
  { phase: 2, name: 'Compatibility Tests' },
  { phase: 3, name: 'Couple Quizzes' },
  { phase: 4, name: 'Love Languages & Personality' },
  { phase: 5, name: 'Couple Games & Questions' },
  { phase: 6, name: 'Relationship Calculators' },
];

export const ALL_TOOLS: ToolItem[] = TOOLS_DATA.map((t) => {
  const seo = getToolSeoConfig(t.id);
  return {
    ...t,
    title: t.name,
    slug: t.id,
    isAvailable: t.isFunctional,
    overview: t.detailedOverview,
    instructions: t.howItWorks,
    metaTitle: seo.title,
    metaDescription: seo.metaDescription,
  };
});


